// src/components/GettingStartedView.js
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  Link,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockIcon from "@mui/icons-material/Lock";
import ResponsiveContainer from "./ResponsiveContainer";
import { useCapabilities } from "../context/CapabilitiesContext";
import { createCheckoutSession } from "../api/billing";
import {
  getProviderKeys,
  getProviderKeysStatus,
  saveProviderKeys,
  getExecutionPolicy,
  saveExecutionPolicy,
  armAutonomousEngine,
  getAutonomousStatus,
} from "../api/settings";

// ---------------------------------------------------------------------------
// Tier feature summaries — plain language, no jargon
// ---------------------------------------------------------------------------
const TIER_SUMMARIES = {
  starter: {
    color: "info",
    included: [
      "Read-only portfolio views and equity curve",
      "Basic performance analytics and drawdown charts",
      "Position overview (no broker sync)",
    ],
    notIncluded: [
      "Screener access",
      "Signals, anomaly detection, and backtesting",
      "Broker sync and paper trading",
      "Autonomous trading engine",
      "Live order execution",
    ],
    upgradeNote: "Upgrade to Advanced or Pro to unlock screener and analytics tools.",
  },
  advanced: {
    color: "info",
    included: [
      "Screener access (up to 10 calls/day, basic filters)",
      "Portfolio analytics and performance history",
      "Basic signals and rankings",
    ],
    notIncluded: [
      "Broker sync",
      "Anomaly detection, backtesting, and rotation analysis",
      "Autonomous trading engine",
      "Live order execution",
    ],
    upgradeNote: "Upgrade to Pro for full analytics, broker sync, and paper trading.",
  },
  pro: {
    color: "success",
    included: [
      "Full screener access (up to 50 calls/day, all filters and rankings)",
      "Complete analytics suite: signals, anomaly detection, backtesting, rotation analysis",
      "Broker sync and portfolio tracking",
      "Paper trading (simulated orders, no real money)",
      "Readiness alerts and risk policy controls",
    ],
    notIncluded: [
      "Autonomous trading engine (scheduled, policy-driven automation)",
      "Live order execution (real money)",
      "Governance audit log and config portability",
    ],
    upgradeNote: "Upgrade to Executive to unlock the autonomous engine and live trading.",
  },
  executive: {
    color: "success",
    included: [
      "Everything in Pro, plus:",
      "Autonomous trading engine — policy-driven, fully automated cycles",
      "Live order execution through your connected brokerage",
      "Unlimited screener calls for automation workflows",
      "Governance audit log, config portability, and advanced policy controls",
      "Full enterprise-grade toolset — every feature is available to you",
    ],
    notIncluded: [],
    upgradeNote: null,
  },
};

// ---------------------------------------------------------------------------
// Provider metadata — what each key does, where to get it, required vs optional
// ---------------------------------------------------------------------------
const PROVIDERS = [
  {
    name: "alpaca",
    secretName: "alpaca_secret",
    label: "Alpaca API Key",
    secretLabel: "Alpaca Secret Key",
    what: "Connects TradePilot to your brokerage. Required for the autonomous trading engine to place and track orders.",
    tier: "required",
    executionOnly: true,
    signupUrl: "https://app.alpaca.markets/signup",
    signupLabel: "Sign up at Alpaca (free paper trading account)",
    howToGet:
      "After signing up, go to Paper Trading → API Keys → Generate New Key. Copy both the API Key and Secret Key — the secret is only shown once.",
  },
  {
    name: "fmp",
    label: "FMP (Financial Modeling Prep) API Key",
    what: "Provides stock fundamentals, earnings, and financial ratios used by the screener and portfolio analysis.",
    tier: "recommended",
    signupUrl: "https://financialmodelingprep.com/developer/docs",
    signupLabel: "Get a free key at FMP",
    howToGet:
      "Sign up, go to your Dashboard, and copy the API key shown on your profile page. The free tier provides enough data for basic use.",
  },
  {
    name: "fred",
    label: "FRED API Key",
    what: "Federal Reserve economic data — used for macro regime detection (e.g. recession signals, interest rate trends).",
    tier: "recommended",
    signupUrl: "https://fred.stlouisfed.org/docs/api/api_key.html",
    signupLabel: "Request a free FRED API key",
    howToGet: "Follow the link, fill in the brief form, and your key will be emailed to you within minutes. It's free.",
  },
  {
    name: "alphavantage",
    label: "Alpha Vantage API Key",
    what: "Backup market data provider for stock prices and technical indicators.",
    tier: "optional",
    signupUrl: "https://www.alphavantage.co/support/#api-key",
    signupLabel: "Get a free Alpha Vantage key",
    howToGet: "Enter your email on the form, and your key is shown immediately. No credit card required.",
  },
  {
    name: "polygon",
    label: "Polygon API Key",
    what: "Premium real-time and historical market data. Provides the broadest tick-level data for advanced screening.",
    tier: "optional",
    signupUrl: "https://polygon.io/dashboard/signup",
    signupLabel: "Sign up at Polygon.io",
    howToGet:
      "After signing up, go to Dashboard → API Keys and copy your key. A free tier is available with some rate limits.",
  },
  {
    name: "finnhub",
    label: "Finnhub API Key",
    what: "Adds stock news, earnings surprises, and quote enrichment to screening results.",
    tier: "optional",
    signupUrl: "https://finnhub.io/register",
    signupLabel: "Get a free Finnhub key",
    howToGet: "Register, and your API key is shown on the dashboard immediately.",
  },
  {
    name: "twelvedata",
    label: "Twelve Data API Key",
    what: "Alternate market data provider used as a fallback for price feeds.",
    tier: "optional",
    signupUrl: "https://twelvedata.com/register",
    signupLabel: "Get a free Twelve Data key",
    howToGet: "Register for a free account and copy your key from the dashboard.",
  },
  {
    name: "binance_api_key",
    secretName: "binance_api_secret",
    label: "Binance API Key",
    secretLabel: "Binance API Secret",
    what: "Syncs your Binance crypto holdings for display in your portfolio. Does not enable automated crypto trading — the autonomous engine operates on equities only.",
    tier: "optional",
    signupUrl: "https://www.binance.com/en/my/settings/api-management",
    signupLabel: "Create an API key in Binance account settings",
    howToGet:
      "Log in to Binance → Account → API Management → Create API. Enable read-only permissions only (no trading permissions needed). Copy the API Key and Secret — the secret is shown once.",
  },
];

const TIER_COLOR = { required: "error", recommended: "warning", optional: "default" };
const TIER_LABEL = { required: "Required", recommended: "Recommended", optional: "Optional" };

// ---------------------------------------------------------------------------
// Tier-specific welcome content — outcomes-first, value-focused
// ---------------------------------------------------------------------------
const TIER_WELCOME = {
  starter: {
    heading: "Welcome to TradePilot Starter",
    tagline: "A clean, focused view of your portfolio — the perfect starting point.",
    outcomes: [
      "Track your portfolio's equity curve and drawdown over time",
      "Review your open positions and overall performance at a glance",
      "Build intuition for how markets move before committing to active trading",
    ],
    quickWins: [
      "Open the Portfolio tab and review your equity curve",
      "Check the Dashboard for your current performance snapshot",
      "Explore what Advanced and Pro offer when you're ready to grow",
    ],
    upgradeHook:
      "Ready to start screening stocks and getting trading signals? Advanced and Pro unlock the full analytical toolkit.",
  },
  advanced: {
    heading: "Welcome to TradePilot Advanced",
    tagline: "A serious analytics toolkit for traders who want an edge.",
    outcomes: [
      "Screen up to 10 stocks daily with filters and rankings to find your best candidates",
      "Track portfolio performance over time with equity curve and drawdown analysis",
      "Monitor trading signals and spot emerging opportunities early",
      "Make better, data-backed decisions on every trade",
    ],
    quickWins: [
      "Connect your API keys (this step) to activate live data",
      "Run your first screener scan and review today's top-ranked stocks",
      "Check the Signals tab to see what the market is flagging right now",
    ],
    upgradeHook:
      "Want broker sync, paper trading, anomaly detection, and backtesting? Upgrade to Pro to unlock the full suite.",
  },
  pro: {
    heading: "Welcome to TradePilot Pro",
    tagline: "The full analytics suite — everything you need to trade with conviction.",
    outcomes: [
      "Screen the full market daily with every filter, ranking, and signal at your disposal",
      "Backtest any strategy against years of historical data before putting capital behind it",
      "Detect market anomalies and rotation opportunities before they become obvious",
      "Sync your broker and execute paper trades to validate your strategy risk-free",
      "Run the full pipeline: screen → analyze → paper trade → review results",
    ],
    quickWins: [
      "Connect your API keys and sync your broker account",
      "Run the screener to find today's highest-ranked candidates",
      "Execute a paper trade to see the full order and position tracking flow",
    ],
    upgradeHook:
      "Want the engine to run your strategy automatically, around the clock, without you needing to be at the screen? That's Executive.",
  },
  executive: {
    heading: "Welcome to TradePilot Executive",
    tagline: "Full enterprise-grade trading automation — set your rules once, let the engine handle the rest.",
    outcomes: [
      "The autonomous engine screens, scores, and trades on your behalf — on a schedule, while you're doing other things",
      "Policy-driven entries and exits: your risk rules are enforced on every single cycle, without emotion",
      "Full analytics suite included: backtest, signals, anomaly detection, rotation analysis, and more",
      "Live or paper execution through your Alpaca brokerage account",
      "Governance audit log, config portability, and complete oversight of every action the engine takes",
    ],
    quickWins: [
      "Connect Alpaca and your data providers (this step)",
      "Run a dry-run cycle to see exactly what the engine would trade today — no orders placed",
      "Arm the engine and watch your first paper position open automatically on the next cycle",
    ],
    upgradeHook: null,
  },
};

// ---------------------------------------------------------------------------
// Step 1 — Welcome
// ---------------------------------------------------------------------------
const NEXT_UPGRADE_TIER = { starter: "advanced", advanced: "pro", pro: "executive" };

function WelcomeStep({ onNext }) {
  const { activeTier, activeTierLabel, loading: tierLoading } = useCapabilities();
  const summary = TIER_SUMMARIES[activeTier] || TIER_SUMMARIES.executive;
  const welcome = TIER_WELCOME[activeTier] || TIER_WELCOME.executive;
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeError, setUpgradeError] = useState("");
  const [billingInterval, setBillingInterval] = useState("monthly");

  const nextTier = NEXT_UPGRADE_TIER[activeTier];
  const nextTierLabel = nextTier
    ? `TradePilot ${nextTier.charAt(0).toUpperCase() + nextTier.slice(1)}`
    : null;

  const handleUpgrade = useCallback(async () => {
    if (!nextTier) return;
    setUpgrading(true);
    setUpgradeError("");
    try {
      const { url } = await createCheckoutSession({ tier: nextTier, interval: billingInterval });
      window.location.href = url;
    } catch {
      setUpgradeError("Could not start checkout. Please try again.");
      setUpgrading(false);
    }
  }, [nextTier, billingInterval]);

  const handleIntervalChange = (_event, nextValue) => {
    if (!nextValue) return;
    setBillingInterval(nextValue);
  };

  return (
    <Stack spacing={3}>
      {/* Tier-specific heading and tagline */}
      <Box>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {tierLoading ? "Welcome to TradePilot" : welcome.heading}
        </Typography>
        {!tierLoading && (
          <Typography variant="body1" color="text.secondary">
            {welcome.tagline}
          </Typography>
        )}
      </Box>

      {/* What you can accomplish on this tier */}
      {!tierLoading && (
        <Card variant="outlined" sx={{ borderColor: `${summary.color}.main` }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                What you can accomplish on
              </Typography>
              <Chip label={activeTierLabel} color={summary.color} size="small" />
            </Box>
            <Stack spacing={0.75}>
              {welcome.outcomes.map((item, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <CheckCircleIcon fontSize="small" color="success" sx={{ mt: 0.2, flexShrink: 0 }} />
                  <Typography variant="body2">{item}</Typography>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Quick wins — first 3 actions */}
      {!tierLoading && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Your first 3 actions
            </Typography>
            <Stack spacing={1}>
              {welcome.quickWins.map((item, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      flexShrink: 0,
                      mt: 0.1,
                    }}
                  >
                    {i + 1}
                  </Box>
                  <Typography variant="body2">{item}</Typography>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* What's locked + upgrade hook */}
      {!tierLoading && summary.notIncluded.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom color="text.secondary">
              Not included on this plan
            </Typography>
            <Stack spacing={0.5} sx={{ mb: welcome.upgradeHook ? 2 : 0 }}>
              {summary.notIncluded.map((item, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <LockIcon fontSize="small" color="disabled" sx={{ mt: 0.2, flexShrink: 0 }} />
                  <Typography variant="body2" color="text.secondary">{item}</Typography>
                </Box>
              ))}
            </Stack>
            {welcome.upgradeHook && (
              <Box sx={{ mt: 1 }}>
                <Alert severity="info" sx={{ mb: nextTier ? 1.5 : 0 }}>
                  {welcome.upgradeHook}
                </Alert>
                {nextTier && (
                  <Box>
                    {upgradeError && (
                      <Alert severity="error" sx={{ mb: 1 }}>{upgradeError}</Alert>
                    )}
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                        Choose billing interval
                      </Typography>
                      <ToggleButtonGroup
                        color="primary"
                        exclusive
                        size="small"
                        value={billingInterval}
                        onChange={handleIntervalChange}
                        aria-label="billing interval"
                      >
                        <ToggleButton value="monthly" aria-label="monthly billing">Monthly</ToggleButton>
                        <ToggleButton value="annual" aria-label="annual billing">Annual</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={upgrading}
                      onClick={handleUpgrade}
                    >
                      {upgrading ? "Opening checkout…" : `Upgrade to ${nextTierLabel}`}
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      <Alert severity="info" icon={<InfoOutlinedIcon />}>
        <strong>First-startup note:</strong> On the very first run, some data providers may briefly
        show connection errors while your keys are being verified for the first time. This is normal
        and clears automatically within a few minutes — no action needed.
      </Alert>

      <Box>
        <Button variant="contained" size="large" onClick={onNext}>
          Let's get started →
        </Button>
      </Box>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — API Keys
// ---------------------------------------------------------------------------
function ProviderKeysStep({ onNext }) {
  const [keys, setKeys] = useState({});
  const [status, setStatus] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const { activeTier, loading: tierLoading } = useCapabilities();
  const hasEngine = !tierLoading && AUTO_TIERS.includes(activeTier);
  const visibleProviders = tierLoading
    ? PROVIDERS
    : PROVIDERS.filter((p) => !p.executionOnly || hasEngine);

  useEffect(() => {
    Promise.all([getProviderKeys(), getProviderKeysStatus()])
      .then(([k, s]) => {
        setKeys(k && typeof k === "object" ? k : {});
        setStatus(s && typeof s === "object" ? s : {});
      })
      .catch(() => {});
  }, []);

  const handleChange = (field, value) => {
    setKeys((prev) => ({ ...prev, [field]: value }));
    setSaveSuccess(false);
    setSaveError("");
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError("");
    setSaveSuccess(false);
    try {
      await saveProviderKeys(keys);
      const s = await getProviderKeysStatus();
      setStatus(s && typeof s === "object" ? s : {});
      setSaveSuccess(true);
    } catch {
      setSaveError("Failed to save keys. Please try again.");
    }
    setSaving(false);
  };

  const requiredConfigured = visibleProviders.filter((p) => p.tier === "required").every(
    (p) => status[p.name]?.configured
  );
  const introText = hasEngine
    ? "TradePilot needs API keys to connect to financial data services and your brokerage. Most are free. Enter as many as you have — you can always come back and add more later."
    : "TradePilot needs API keys to connect to financial data services. Most are free. Enter as many as you have — you can always come back and add more later.";

  return (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary">
        {introText}
      </Typography>

      <Alert severity="info" icon={<InfoOutlinedIcon />}>
        Your keys are stored securely on your server and are never shared or transmitted to third parties.
      </Alert>

      {visibleProviders.map((provider) => {
        const isConfigured = status[provider.name]?.configured;
        const isExpanded = expanded === provider.name;

        return (
          <Card
            key={provider.name}
            variant="outlined"
            sx={{ borderColor: isConfigured ? "success.main" : undefined, opacity: 1 }}
          >
            <CardContent sx={{ pb: "12px !important" }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  {isConfigured ? (
                    <CheckCircleIcon fontSize="small" color="success" />
                  ) : (
                    <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
                  )}
                  <Typography variant="subtitle2" fontWeight={600}>
                    {provider.label}
                  </Typography>
                  <Chip
                    label={TIER_LABEL[provider.tier]}
                    color={TIER_COLOR[provider.tier]}
                    size="small"
                    variant="outlined"
                  />
                  {isConfigured && (
                    <Chip label="Configured" color="success" size="small" />
                  )}
                </Box>
                <Button
                  size="small"
                  onClick={() => setExpanded(isExpanded ? null : provider.name)}
                  sx={{ flexShrink: 0 }}
                >
                  {isExpanded ? "Collapse" : "Set up"}
                </Button>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ ml: 3.5 }}>
                {provider.what}
              </Typography>

              {isExpanded && (
                <Box sx={{ mt: 2, ml: 3.5 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <strong>How to get this key:</strong> {provider.howToGet}
                    <Box sx={{ mt: 0.75 }}>
                      <Link
                        href={provider.signupUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                      >
                        {provider.signupLabel} <OpenInNewIcon fontSize="inherit" />
                      </Link>
                    </Box>
                  </Alert>

                  <TextField
                    label={provider.label}
                    value={keys[provider.name] || ""}
                    onChange={(e) => handleChange(provider.name, e.target.value)}
                    fullWidth
                    size="small"
                    type="password"
                    autoComplete="off"
                    sx={{ mb: provider.secretName ? 1.5 : 0 }}
                    InputProps={{
                      endAdornment: keys[provider.name] ? (
                        <InputAdornment position="end">
                          <CheckCircleIcon fontSize="small" color="success" />
                        </InputAdornment>
                      ) : null,
                    }}
                  />

                  {provider.secretName && (
                    <TextField
                      label={provider.secretLabel}
                      value={keys[provider.secretName] || ""}
                      onChange={(e) => handleChange(provider.secretName, e.target.value)}
                      fullWidth
                      size="small"
                      type="password"
                      autoComplete="off"
                      helperText="The secret key is only shown once when you generate it. Store it somewhere safe."
                    />
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        );
      })}

      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? "Saving…" : "Save Keys"}
        </Button>
        {saveSuccess && <Typography variant="body2" color="success.main">Keys saved.</Typography>}
        {saveError && <Typography variant="body2" color="error.main">{saveError}</Typography>}
      </Stack>

      {!requiredConfigured && (
        <Alert severity="warning">
          Alpaca is required for the autonomous engine. You can continue without it now, but trading
          will not work until it's configured.
        </Alert>
      )}

      <Box>
        <Button variant="contained" onClick={onNext}>
          Continue →
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
          You can always update keys later in Settings.
        </Typography>
      </Box>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — Capital Guidance
// ---------------------------------------------------------------------------
function CapitalStep({ onNext }) {
  return (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary">
        TradePilot sizes each position as a percentage of your declared starting capital. Understanding
        this helps you set realistic expectations for returns.
      </Typography>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            How position sizing works
          </Typography>
          <Stack spacing={1}>
            {[
              { label: "High-confidence pick (score ≥ 60)", pct: "3%", eg100k: "$3,000", eg25k: "$750" },
              { label: "Medium-confidence pick (score ≥ 40)", pct: "2%", eg100k: "$2,000", eg25k: "$500" },
              { label: "Low-confidence pick", pct: "1%", eg100k: "$1,000", eg25k: "$250" },
            ].map((row) => (
              <Box
                key={row.label}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto auto",
                  gap: 2,
                  alignItems: "center",
                  py: 0.5,
                }}
              >
                <Typography variant="body2">{row.label}</Typography>
                <Chip label={row.pct} size="small" />
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, textAlign: "right" }}>
                  {row.eg100k} @ $100k
                </Typography>
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ borderColor: "warning.main" }}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom color="warning.main">
            Capital thresholds
          </Typography>
          <Stack spacing={1.5}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <Chip label="< $10,000" color="error" size="small" sx={{ mt: 0.25 }} />
              <Typography variant="body2" color="text.secondary">
                Not recommended. Position sizes will be too small to be meaningful and may fall below
                broker minimum order values.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <Chip label="$10,000 – $25,000" color="warning" size="small" sx={{ mt: 0.25 }} />
              <Typography variant="body2" color="text.secondary">
                Workable for paper trading and learning. Returns will be modest. Good for testing
                the engine before committing larger capital.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <Chip label="$25,000+" color="success" size="small" sx={{ mt: 0.25 }} />
              <Typography variant="body2" color="text.secondary">
                Recommended for meaningful returns. Positions are large enough to offset fees and
                capture real price movements.
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Alert severity="info">
        <strong>Paper trading first:</strong> We strongly recommend running in paper trading mode
        (simulated orders, no real money) for at least a few weeks before switching to live trading.
        This lets you verify the engine's behavior with your specific capital level and risk settings.
      </Alert>

      <Typography variant="body2" color="text.secondary">
        Your starting capital is set in <strong>Settings → Portfolio Seed</strong>. The default is
        $100,000 for paper trading. You can adjust it to match what you plan to invest.
      </Typography>

      <Box>
        <Button variant="contained" onClick={onNext}>
          Continue →
        </Button>
      </Box>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Step 4 — Engine Mode
// ---------------------------------------------------------------------------
function EngineModeStep({ onNext }) {
  const [policy, setPolicy] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    getExecutionPolicy()
      .then((p) => setPolicy(p && typeof p === "object" ? p : {}))
      .catch(() => setPolicy({}));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaveError("");
    setSaveSuccess(false);
    try {
      await saveExecutionPolicy(policy);
      setSaveSuccess(true);
    } catch {
      setSaveError("Failed to save. Please try again.");
    }
    setSaving(false);
  };

  if (policy === null) {
    return <CircularProgress size={24} />;
  }

  const isPaper = policy.paper_mode !== false;
  const isDryRun = policy.dry_run === true;
  const isLive = policy.live_execution === true;

  return (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary">
        Choose how the autonomous engine runs. Start with paper trading — you can switch to live
        trading at any time once you're comfortable.
      </Typography>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Trading mode
          </Typography>
          <Stack spacing={2}>
            <Box
              onClick={() => setPolicy((p) => ({ ...p, paper_mode: true, live_execution: false, dry_run: false }))}
              sx={{
                cursor: "pointer",
                p: 2,
                borderRadius: 1,
                border: "2px solid",
                borderColor: isPaper && !isDryRun ? "primary.main" : "divider",
                bgcolor: isPaper && !isDryRun ? "primary.50" : "transparent",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                {isPaper && !isDryRun ? <CheckCircleIcon color="primary" fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
                <Typography variant="subtitle2" fontWeight={600}>Paper Trading (recommended to start)</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 3.5 }}>
                The engine runs fully — scoring stocks, entering and exiting positions — but all orders
                go to a simulated brokerage account. No real money moves. Perfect for learning and validating
                the engine's behavior.
              </Typography>
            </Box>

            <Box
              onClick={() => setPolicy((p) => ({ ...p, dry_run: true, paper_mode: true, live_execution: false }))}
              sx={{
                cursor: "pointer",
                p: 2,
                borderRadius: 1,
                border: "2px solid",
                borderColor: isDryRun ? "primary.main" : "divider",
                bgcolor: isDryRun ? "primary.50" : "transparent",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                {isDryRun ? <CheckCircleIcon color="primary" fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
                <Typography variant="subtitle2" fontWeight={600}>Dry Run (observation only)</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 3.5 }}>
                The engine scores stocks and evaluates trades but takes no action at all. Use this to
                watch what the engine <em>would</em> do before letting it trade, even on paper.
              </Typography>
            </Box>

            <Box
              onClick={() => setPolicy((p) => ({ ...p, live_execution: true, paper_mode: false, dry_run: false }))}
              sx={{
                cursor: "pointer",
                p: 2,
                borderRadius: 1,
                border: "2px solid",
                borderColor: isLive ? "error.main" : "divider",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                {isLive ? <CheckCircleIcon color="error" fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
                <Typography variant="subtitle2" fontWeight={600} color={isLive ? "error.main" : "text.primary"}>
                  Live Trading
                </Typography>
                <Chip label="Real money" color="error" size="small" variant="outlined" />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 3.5 }}>
                Orders go to your real brokerage account with real money. Only enable this after you
                have validated the engine in paper mode and are confident in your risk settings.
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {isLive && (
        <Alert severity="error">
          <strong>Live trading is enabled.</strong> Real orders will be placed with real money. Make
          sure your risk policy and position sizing are exactly as intended before arming the engine.
        </Alert>
      )}

      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? "Saving…" : "Save Mode"}
        </Button>
        {saveSuccess && <Typography variant="body2" color="success.main">Saved.</Typography>}
        {saveError && <Typography variant="body2" color="error">{saveError}</Typography>}
      </Stack>

      <Box>
        <Button variant="contained" onClick={onNext}>
          Continue →
        </Button>
      </Box>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Step 5 — Arm & Go
// ---------------------------------------------------------------------------
function ArmStep() {
  const [status, setStatus] = useState(null);
  const [reason, setReason] = useState("Initial setup");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchStatus = useCallback(() => {
    getAutonomousStatus()
      .then((s) => setStatus(s && typeof s === "object" ? s : null))
      .catch(() => setStatus(null));
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleArm = async () => {
    setLoading(true);
    setError("");
    try {
      await armAutonomousEngine(reason || "Setup wizard");
      fetchStatus();
    } catch (e) {
      setError(e?.message || "Failed to arm engine.");
    }
    setLoading(false);
  };

  const isArmed = status?.armed === true;

  return (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary">
        The last step is arming the engine. When armed, the engine will execute its trading cycle on
        schedule. You can disarm it at any time from Settings.
      </Typography>

      <Card variant="outlined" sx={{ borderColor: isArmed ? "success.main" : undefined }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
            {isArmed ? (
              <LockOpenIcon color="success" />
            ) : (
              <LockIcon color="disabled" />
            )}
            <Typography variant="subtitle2" fontWeight={700}>
              Engine Status: {status === null ? "Checking…" : isArmed ? "Armed" : "Disarmed"}
            </Typography>
            {isArmed && <Chip label="Ready" color="success" size="small" />}
          </Box>

          {isArmed ? (
            <Alert severity="success">
              The engine is armed and ready. It will run automatically on its next scheduled cycle.
              You can monitor activity in the <strong>Paper Positions</strong> and{" "}
              <strong>Dashboard</strong> tabs.
            </Alert>
          ) : (
            <Stack spacing={2}>
              <TextField
                label="Reason for arming (optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                fullWidth
                size="small"
                helperText="A short note logged with the arm event for your records."
              />
              <Box>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleArm}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={16} /> : <LockOpenIcon />}
                >
                  {loading ? "Arming…" : "Arm the Engine"}
                </Button>
              </Box>
              {error && <Alert severity="error">{error}</Alert>}
            </Stack>
          )}
        </CardContent>
      </Card>

      {isArmed && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              You're all set. Here's what happens next:
            </Typography>
            <Stack spacing={1}>
              {[
                "The engine scores stocks from the screener universe on each cycle",
                "When a high-confidence pick appears, it enters a position",
                "Positions are monitored for exit signals (take-profit, stop-loss, regime shift)",
                "All activity is logged — check Paper Positions and the Dashboard to follow along",
                "You can disarm the engine at any time in Settings → Autonomous Engine",
              ].map((item, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <CheckCircleIcon fontSize="small" color="success" sx={{ mt: 0.25 }} />
                  <Typography variant="body2">{item}</Typography>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------
// Steps with automationOnly:true are hidden for Starter and Advanced tiers
const ALL_STEPS = [
  { label: "Welcome", description: "Overview of TradePilot and what we'll set up", automationOnly: false },
  { label: "Connect Providers", description: "Enter your API keys for data and brokerage", automationOnly: false },
  { label: "Capital Guidance", description: "Understand position sizing and capital requirements", automationOnly: false },
  { label: "Engine Mode", description: "Choose paper trading, dry run, or live trading", automationOnly: true },
  { label: "Arm & Go", description: "Arm the engine and start your first cycle", automationOnly: true },
];

const AUTO_TIERS = ["pro", "executive"];

export default function GettingStartedView() {
  const [activeStep, setActiveStep] = useState(0);
  const { activeTier, loading: tierLoading } = useCapabilities();

  const hasEngine = !tierLoading && AUTO_TIERS.includes(activeTier);
  // Show all steps while tier loads to avoid layout jump; filter once known
  const steps = tierLoading
    ? ALL_STEPS
    : ALL_STEPS.filter((s) => !s.automationOnly || hasEngine);

  const handleNext = () => setActiveStep((s) => Math.min(s + 1, steps.length - 1));
  const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  const allContent = [
    <WelcomeStep key="welcome" onNext={handleNext} />,
    <ProviderKeysStep key="keys" onNext={handleNext} />,
    <CapitalStep key="capital" onNext={handleNext} />,
    <EngineModeStep key="mode" onNext={handleNext} />,
    <ArmStep key="arm" />,
  ];
  const stepContent = tierLoading
    ? allContent
    : ALL_STEPS.reduce((acc, s, i) => {
        if (!s.automationOnly || hasEngine) acc.push(allContent[i]);
        return acc;
      }, []);

  // Clamp active step in case tier loads and shrinks the visible step count
  const clampedStep = Math.min(activeStep, steps.length - 1);

  return (
    <ResponsiveContainer>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Getting Started
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Follow these steps to configure TradePilot. You can return to this page at any time.
        </Typography>
      </Box>

      <Stepper activeStep={clampedStep} orientation="vertical">
        {steps.map((step, index) => (
          <Step key={step.label} completed={index < clampedStep}>
            <StepLabel
              onClick={() => setActiveStep(index)}
              sx={{ cursor: "pointer" }}
              optional={
                <Typography variant="caption" color="text.secondary">
                  {step.description}
                </Typography>
              }
            >
              <Typography variant="subtitle1" fontWeight={index === clampedStep ? 700 : 400}>
                {step.label}
              </Typography>
            </StepLabel>
            <StepContent>
              <Box sx={{ py: 2 }}>{stepContent[index]}</Box>
              {index > 0 && (
                <Button size="small" onClick={handleBack} sx={{ mt: 1 }}>
                  ← Back
                </Button>
              )}
            </StepContent>
          </Step>
        ))}
      </Stepper>
    </ResponsiveContainer>
  );
}
